// src/features/automacaodeemails/pages/AutomationsPage.jsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";

const ICONS = {
  plus: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  ),
  play: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polygon points="5,3 19,12 5,21 5,3" />
    </svg>
  ),
  pause: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="6" y="4" width="4" height="16" />
      <rect x="14" y="4" width="4" height="16" />
    </svg>
  ),
  edit: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  ),
  trash: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="3,6 5,6 21,6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  ),
  send: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22,2 15,22 11,13 2,9 22,2" />
    </svg>
  ),
  loader: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="ae2-spin">
      <line x1="12" y1="2" x2="12" y2="6" />
      <line x1="12" y1="18" x2="12" y2="22" />
      <line x1="4.93" y1="4.93" x2="7.76" y2="7.76" />
      <line x1="16.24" y1="16.24" x2="19.07" y2="19.07" />
    </svg>
  ),
  eye: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ),
  x: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
};

const FREQ_LABELS = {
  DAILY: "Diário",
  WEEKLY: "Semanal",
  MONTHLY: "Mensal",
  CUSTOM: "Customizado",
};

export default function AutomationsPage() {
  const nav = useNavigate();
  const [automations, setAutomations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [previewAuto, setPreviewAuto] = useState(null);
  const [previewCtx, setPreviewCtx] = useState(null);

  const loadAutomations = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.automations.list();
      setAutomations(res?.data || []);
    } catch (err) {
      console.error("Erro ao carregar automacoes:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAutomations();
  }, [loadAutomations]);

  async function handleToggle(auto) {
    setActionLoading(auto.id);
    try {
      await api.automations.toggle(auto.id);
      loadAutomations();
    } catch (err) {
      alert(`Erro: ${err?.message}`);
    } finally {
      setActionLoading(null);
    }
  }

  async function handleExecute(auto) {
    if (!window.confirm(`Executar automação "${auto.nome}" agora?`)) return;
    setActionLoading(auto.id);
    try {
      const res = await api.automations.execute(auto.id);
      alert(`Enviado com sucesso para ${res?.data?.destinatarios || 0} destinatário(s)`);
      loadAutomations();
    } catch (err) {
      alert(`Erro: ${err?.message}`);
    } finally {
      setActionLoading(null);
    }
  }

  async function handleDelete(auto) {
    if (!window.confirm(`Excluir automação "${auto.nome}"?`)) return;
    try {
      await api.automations.delete(auto.id);
      loadAutomations();
    } catch (err) {
      alert(`Erro: ${err?.message}`);
    }
  }

  function applyPlaceholders(text, ctx) {
    return String(text || "")
      .replace(/\$\{(\w+)\}/g, (_, k) => (ctx[k] != null ? String(ctx[k]) : `\$\{${k}\}`))
      .replace(/\{\{(\w+)\}\}/g, (_, k) => (ctx[k] != null ? String(ctx[k]) : `{{${k}}}`));
  }

  function escapeHtml(s) {
    return String(s || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
  }

  function markdownToPreviewHtml(md) {
    let src = escapeHtml(String(md || ""));
    src = src.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
    src = src.replace(/\*(.*?)\*/g, "<em>$1</em>");
    src = src.replace(/`([^`]+)`/g, "<code>$1</code>");
    src = src.replace(/^### (.*)$/gm, "<h3>$1</h3>");
    src = src.replace(/^## (.*)$/gm, "<h2>$1</h2>");
    src = src.replace(/^# (.*)$/gm, "<h1>$1</h1>");
    src = src.replace(/\n/g, "<br/>");
    return src;
  }

  const SIGN_TOKEN = "__SIGNATURE__";
  const SIGN_IMG_TOKEN = "__SIGNATURE_IMG__";
  const SIGN_TEXT_TOKEN = "__SIGNATURE_TEXT__";
  const SIGN_EXTRA_TOKEN = "__SIGNATURE_EXTRA__";

  function buildSignaturePreviewParts(auto, ctx) {
    if (!auto) return { full: "", img: "", text: "", extra: "" };
    const signatureActive = auto.assinatura_ativa === 1 || auto.assinatura_ativa === true;
    if (!signatureActive) return { full: "", img: "", text: "", extra: "" };

    const textHtml = auto.assinatura_md
      ? markdownToPreviewHtml(applyPlaceholders(auto.assinatura_md, ctx))
      : "";
    const imgHtml =
      auto.assinatura_img_base64 && auto.assinatura_img_mime
        ? `<img src="data:${auto.assinatura_img_mime};base64,${auto.assinatura_img_base64}" alt="Assinatura" />`
        : "";
    const extraHtml =
      auto.assinatura_extra_img_base64 && auto.assinatura_extra_img_mime
        ? `<img src="data:${auto.assinatura_extra_img_mime};base64,${auto.assinatura_extra_img_base64}" alt="Assinatura extra" class="ae2-email-signature-extra" />`
        : "";
    if (!textHtml && !imgHtml && !extraHtml) return { full: "", img: "", text: "", extra: "" };
    const full = `
      <div class="ae2-email-signature">
        ${textHtml ? `<div class="ae2-email-text">${textHtml}</div>` : ""}
        ${imgHtml || ""}
        ${extraHtml || ""}
      </div>
    `;
    return { full, img: imgHtml, text: textHtml, extra: extraHtml };
  }

  function applySignaturePlaceholders(bodyHtml, parts) {
    const tokenized = String(bodyHtml || "")
      .replace(/\{\{\s*assinatura\s*\}\}/gi, SIGN_TOKEN)
      .replace(/\{\{\s*assinatura_img\s*\}\}/gi, SIGN_IMG_TOKEN)
      .replace(/\{\{\s*assinatura_texto\s*\}\}/gi, SIGN_TEXT_TOKEN)
      .replace(/\{\{\s*assinatura_text\s*\}\}/gi, SIGN_TEXT_TOKEN)
      .replace(/\{\{\s*assinatura_extra\s*\}\}/gi, SIGN_EXTRA_TOKEN);

    if (!parts.full) {
      return {
        html: tokenized
          .replaceAll(SIGN_TOKEN, "")
          .replaceAll(SIGN_IMG_TOKEN, "")
          .replaceAll(SIGN_TEXT_TOKEN, "")
          .replaceAll(SIGN_EXTRA_TOKEN, ""),
        placed: false,
      };
    }

    const hasToken =
      tokenized.includes(SIGN_TOKEN) ||
      tokenized.includes(SIGN_IMG_TOKEN) ||
      tokenized.includes(SIGN_TEXT_TOKEN) ||
      tokenized.includes(SIGN_EXTRA_TOKEN);

    if (!hasToken) return { html: tokenized, placed: false };

    return {
      html: tokenized
        .replaceAll(SIGN_TOKEN, parts.full)
        .replaceAll(SIGN_IMG_TOKEN, parts.img || "")
        .replaceAll(SIGN_TEXT_TOKEN, parts.text || "")
        .replaceAll(SIGN_EXTRA_TOKEN, parts.extra || ""),
      placed: true,
    };
  }

  async function openPreview(auto) {
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewError("");
    const now = new Date();
    setPreviewCtx({
      data: now.toLocaleDateString("pt-BR"),
      hora: now.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }),
      dia_semana: now.toLocaleDateString("pt-BR", { weekday: "long" }),
    });

    try {
      const res = await api.automations.get(auto.id);
      setPreviewAuto(res?.data || null);
    } catch (err) {
      setPreviewError(err?.message || "Erro ao carregar preview");
      setPreviewAuto(null);
    } finally {
      setPreviewLoading(false);
    }
  }

  const previewSignatureParts = useMemo(
    () => buildSignaturePreviewParts(previewAuto, previewCtx || {}),
    [previewAuto, previewCtx]
  );

  const previewBody = useMemo(() => {
    if (!previewAuto?.corpo_md || !previewCtx) return { html: "", placed: false };
    const raw = markdownToPreviewHtml(applyPlaceholders(previewAuto.corpo_md, previewCtx));
    return applySignaturePlaceholders(raw, previewSignatureParts);
  }, [previewAuto, previewCtx, previewSignatureParts]);

  return (
    <div className="ae2-page">
      <div className="ae2-page-header">
        <div>
          <h2>Minhas automações</h2>
          <p>Gerencie status, execute manualmente e edite configurações.</p>
        </div>
        <div className="ae2-actions" />
      </div>

      {loading ? (
        <div className="ae2-loading">Carregando...</div>
      ) : automations.length === 0 ? (
        <div className="ae2-empty">
          <h3>Nenhuma automação configurada</h3>
          <p>Crie sua primeira automação de e-mail.</p>
        </div>
      ) : (
        <div className="ae2-card ae2-table-wrap">
          <table className="ae2-table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Frequencia</th>
                <th>Horario</th>
                <th>Elementos</th>
                <th>Status</th>
                <th>Proximo envio</th>
                <th>Acoes</th>
              </tr>
            </thead>
            <tbody>
              {automations.map((auto) => (
                <tr key={auto.id}>
                  <td>
                    <div className="ae2-cell">
                      <div className="ae2-cell-title">{auto.nome}</div>
                      <div className="ae2-cell-sub">{auto.assunto}</div>
                    </div>
                  </td>
                  <td>{FREQ_LABELS[auto.freq] || auto.freq}</td>
                  <td>{auto.hora}</td>
                  <td>{auto.elements_count || 0}</td>
                  <td>
                    <span className={`ae2-badge ${auto.status === "active" ? "success" : auto.status === "paused" ? "warning" : "danger"}`}>
                      {auto.status === "active" ? "Ativo" : auto.status === "paused" ? "Pausado" : "Erro"}
                    </span>
                  </td>
                  <td>
                    {auto.next_run_at ? new Date(auto.next_run_at).toLocaleString("pt-BR") : "-"}
                  </td>
                  <td>
                    <div className="ae2-inline-actions">
                      <button
                        className="ae2-btn ae2-btn-ghost"
                        title="Preview do e-mail"
                        onClick={() => openPreview(auto)}
                      >
                        {ICONS.eye}
                      </button>
                      <button
                        className="ae2-btn ae2-btn-ghost"
                        title={auto.status === "active" ? "Pausar" : "Ativar"}
                        onClick={() => handleToggle(auto)}
                        disabled={actionLoading === auto.id}
                      >
                        {actionLoading === auto.id ? ICONS.loader : (auto.status === "active" ? ICONS.pause : ICONS.play)}
                      </button>
                      <button
                        className="ae2-btn ae2-btn-ghost"
                        title="Executar agora"
                        onClick={() => handleExecute(auto)}
                        disabled={actionLoading === auto.id}
                      >
                        {ICONS.send}
                      </button>
                      <button
                        className="ae2-btn ae2-btn-ghost"
                        title="Editar"
                        onClick={() => nav(`/automacao-emails/wizard/${auto.id}`)}
                      >
                        {ICONS.edit}
                      </button>
                      <button
                        className="ae2-btn ae2-btn-danger"
                        title="Excluir"
                        onClick={() => handleDelete(auto)}
                      >
                        {ICONS.trash}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {previewOpen && (
        <div className="ae2-modal-overlay" onClick={() => setPreviewOpen(false)}>
          <div className="ae2-modal ae2-modal-wide" onClick={(e) => e.stopPropagation()}>
            <div className="ae2-modal-header">
              <div>
                <h2>Preview do e-mail</h2>
              <div className="ae2-modal-sub">{previewAuto?.nome || "Automação"}</div>
              </div>
              <button className="ae2-btn ae2-btn-glass" onClick={() => setPreviewOpen(false)}>
                {ICONS.x}
              </button>
            </div>
            <div className="ae2-modal-body">
              {previewError && <div className="ae2-info error">{previewError}</div>}
              {previewLoading && <div className="ae2-info">Carregando preview...</div>}

              {previewAuto && previewCtx && (
                <div className="ae2-email-preview">
                  <div className="ae2-email-header">
                    <div className="ae2-email-subject">
                      {applyPlaceholders(previewAuto.assunto || "(sem assunto)", previewCtx)}
                    </div>
                    <div className="ae2-email-meta">
                      Gerado automaticamente em {previewCtx.data} as {previewCtx.hora}
                    </div>
                  </div>
                  <div className="ae2-email-body">
                    {previewAuto.corpo_md ? (
                      <div
                        className="ae2-email-text"
                        dangerouslySetInnerHTML={{
                          __html: previewBody.html,
                        }}
                      />
                    ) : (
                      <div className="ae2-muted">Sem corpo de mensagem.</div>
                    )}

                    {Array.isArray(previewAuto.elements) && previewAuto.elements.length > 0 && (
                      <div className="ae2-email-section">
                        <h4>Dados capturados</h4>
                        {previewAuto.elements.map((el) => (
                          <div key={el.element_id} className="ae2-email-element">
                            <div className="ae2-email-element-title">
                              {el.auto_name} <span>({el.screen_nome})</span>
                            </div>
                            {el.tipo === "table" ? (
                              <div className="ae2-email-element-image">
                                <img
                                  src={`${api.elements.previewUrl(el.element_id, "image")}&t=${Date.now()}`}
                                  alt={el.auto_name}
                                />
                              </div>
                            ) : (
                              <div className="ae2-email-element-image">
                                <img
                                  src={`${api.elements.previewUrl(el.element_id, "image")}&t=${Date.now()}`}
                                  alt={el.auto_name}
                                />
                              </div>
                            )}
                            <div className="ae2-email-element-meta">{el.dimensions || "-"}</div>
                          </div>
                        ))}
                      </div>
                    )}

                    {!previewBody.placed &&
                      (previewAuto.assinatura_ativa === 1 || previewAuto.assinatura_ativa === true) &&
                      (previewAuto.assinatura_md ||
                        previewAuto.assinatura_img_base64 ||
                        previewAuto.assinatura_extra_img_base64) && (
                      <div className="ae2-email-signature">
                        {previewAuto.assinatura_md && (
                          <div
                            className="ae2-email-text"
                            dangerouslySetInnerHTML={{
                              __html: markdownToPreviewHtml(
                                applyPlaceholders(previewAuto.assinatura_md, previewCtx)
                              ),
                            }}
                          />
                        )}
                        {previewAuto.assinatura_img_base64 && previewAuto.assinatura_img_mime && (
                          <img
                            src={`data:${previewAuto.assinatura_img_mime};base64,${previewAuto.assinatura_img_base64}`}
                            alt="Assinatura"
                          />
                        )}
                        {previewAuto.assinatura_extra_img_base64 && previewAuto.assinatura_extra_img_mime && (
                          <img
                            src={`data:${previewAuto.assinatura_extra_img_mime};base64,${previewAuto.assinatura_extra_img_base64}`}
                            alt="Assinatura extra"
                            className="ae2-email-signature-extra"
                          />
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="ae2-modal-footer">
              <button className="ae2-btn ae2-btn-ghost" onClick={() => setPreviewOpen(false)}>
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
